import { retryableQuery } from '../../../functions/retryableQuery';
import { db } from '../../../db';
import { eq, and, inArray, or } from 'drizzle-orm';
import { roleResourcePermissions } from '../models/roleResourcePermissions';
import { RoleResourcePermission } from '../types/RoleResourcePermission';
import { userRoles } from '../models/userRoles';
import { BaseRepository } from '../../../classes/BaseRepository';
import { AppError, AsyncResult, err, ok } from '@shared/utils';
import { DATA_ACCESS_LEVELS } from '@shared/utils/src/core/constants/DATA_ACCESS_LEVELS';
import { DataAccessLevel } from '@shared/utils/src/core/types/DataAccessLevel';
import { StaticResource } from '@shared/utils/src/core/types/StaticResource';
import { PermissionVerb } from '@shared/utils/src/core/types/PermissionVerb';
import { permissionOrder } from '@shared/utils/src/core/functions/permissionOrder';

/**
 * @group RoleResourcePermission
 * 
 * @summary
 * <blockquote>Repository for managing role-based resource permissions and user role relationships.</blockquote>
 * 
 * <blockquote>
 * Provides data access methods for role resource permissions, user role assignments, and permission checking.
 * Extends BaseRepository to inherit common CRUD operations while adding domain-specific query methods.
 * </blockquote>
 * 
 * @example
 * ```typescript
 * const repository = new RoleResourcePermissionRepository();
 * 
 * // Get permissions for specific roles
 * const permissions = await repository.getRolePermissions(['role-123', 'role-456']);
 * 
 * // Check if user has a specific role
 * const hasRole = await repository.checkUserHasRole('user-123', 'role-456');
 * ```
 */
export class RoleResourcePermissionRepository extends BaseRepository<RoleResourcePermission> {
  protected table = roleResourcePermissions;

  /**
   * @summary
   * <blockquote>Retrieves all resource permissions for the specified role IDs.</blockquote>
   * 
   * @param roleIds - Array of role identifiers to fetch permissions for
   * @returns Promise resolving to role resource permissions or error
   * 
   * @example
   * ```typescript
   * const permissions = await repository.getRolePermissions(['role-123', 'role-456']);
   * 
   * ```
   * 
   * @returns AsyncResult containing role permissions or AppError if database query fails or connection issues occur
   */
  async getRolePermissions(roleIds: string[]): AsyncResult<RoleResourcePermission[], AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery(() =>
          database.select().from(this.table).where(inArray(this.table.role_id, roleIds))
        );

        return queryResult.match({
          ok: (rolePermissions) => ok(rolePermissions),
          err: (error) =>
            err({
              errorMessage: error instanceof Error ? error.message : String(error),
            }),
        });
      },
      err: async (error) => err(error)
    });
  }


  /**
   * @summary
   * <blockquote>Checks if a user has been assigned a specific role.</blockquote>
   * 
   * @param userId - The user identifier to check
   * @param roleId - The role identifier to verify
   * @returns Promise resolving to boolean indicating role assignment or error
   * 
   * @example
   * ```typescript
   * const hasRole = await repository.checkUserHasRole('user-123', 'role-456');
   * 
   * if (hasRole.isOk()) {
   *   if (hasRole.value) {
   *     console.log('User has the specified role');
   *   } else {
   *     console.log('User does not have the specified role');
   *   }
   * }
   * ```
   * 
   * @returns AsyncResult containing boolean or AppError if database query fails or connection issues occur
   */
  async checkUserHasRole(userId: string, roleId: string): AsyncResult<boolean, AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery(() =>
          database
            .select()
            .from(userRoles)
            .where(and(eq(userRoles.user_id, userId), eq(userRoles.role_id, roleId)))
        );

        return queryResult.match({
          ok: (rows) => ok(rows.length > 0),
          err: (error) =>
            err({
              errorMessage: error instanceof Error ? error.message : String(error),
            }),
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * @summary
   * <blockquote>Resolves the highest effective data access level for a user, resource, and verb combination.</blockquote>
   * 
   * This method retrieves all roles assigned to the user (across all tenants), fetches their permissions,
   * filters by resource and verb, and returns the highest data access level based on the hierarchy:
   * `all > role > tenant > own > none`.
   * 
   * @param userId - The user identifier to check permissions for
   * @param resource - The resource identifier to check access for
   * @param verb - The permission verb to check
   * @returns AsyncResult containing an object with effectiveDataAccessLevel and effectiveRoleId, or error
   * 
   * @example
   * ```typescript
   * const resolution = await repository.getHighestDataAccessForResource(
   *   'user-123',
   *   ROUTE_RESOURCES.API_DATA_USERS,
   *   PERMISSION_VERBS.READ
   * );
   * 
   * if (resolution.isOk()) {
   *   const { effectiveDataAccessLevel, effectiveRoleId } = resolution.value;
   *   console.log(`Highest access level: ${effectiveDataAccessLevel} from role: ${effectiveRoleId}`);
   * }
   * ```
   * 
   * @returns AsyncResult containing access resolution with effectiveDataAccessLevel and effectiveRoleId, or AppError
   */
  async getHighestDataAccessForResource(
    userId: string,
    resource: StaticResource,
    verb: PermissionVerb
  ): AsyncResult<{ effectiveDataAccessLevel: DataAccessLevel; effectiveRoleId: string | null }, AppError> {
    if (!userId || !resource || !verb) {
      return err({
        errorMessage: 'User ID, resource, and verb are required',
      });
    }

    return db.match({
      ok: async (database) => {
        // Get all user roles (across all tenants)
        const userRolesResult = await retryableQuery(() =>
          database
            .select({ role_id: userRoles.role_id })
            .from(userRoles)
            .where(eq(userRoles.user_id, userId))
        );

        if (userRolesResult.isErr) {
          return err({
            errorMessage: userRolesResult.error instanceof Error 
              ? userRolesResult.error.message 
              : String(userRolesResult.error),
          });
        }

        const userRoleRows = userRolesResult.getSuccess();
        
        // If user has no roles, return 'none' access level
        if (userRoleRows.length === 0) {
          return ok({
            effectiveDataAccessLevel: DATA_ACCESS_LEVELS.NONE as DataAccessLevel,
            effectiveRoleId: null,
          });
        }

        const roleIds = userRoleRows.map(row => row.role_id);

        // Get permissions for all user roles
        const permissionsResult = await this.getRolePermissions(roleIds);
        if (permissionsResult.isErr) {
          return err(permissionsResult.getFail());
        }

        const permissions = permissionsResult.getSuccess();

        // Filter permissions by resource and verb (checking verb hierarchy)
        const relevantPermissions = permissions.filter(permission => {
          const resourceMatches = permission.resource === resource;
          const verbMatches = this.verbMatches(permission.verb as PermissionVerb, verb);
          return resourceMatches && verbMatches;
        });

        // If no matching permissions, return 'none' access level
        if (relevantPermissions.length === 0) {
          return ok({
            effectiveDataAccessLevel: DATA_ACCESS_LEVELS.NONE as DataAccessLevel,
            effectiveRoleId: null,
          });
        }

        // Define data access level hierarchy (most to least permissive)
        const accessLevelHierarchy: DataAccessLevel[] = [
          DATA_ACCESS_LEVELS.ALL,
          DATA_ACCESS_LEVELS.ROLE,
          DATA_ACCESS_LEVELS.TENANT,
          DATA_ACCESS_LEVELS.OWN,
          DATA_ACCESS_LEVELS.NONE,
        ];

        // Find the most permissive permission
        const mostPermissivePermission = relevantPermissions.reduce((mostPermissive, current) => {
          const mostPermissiveIndex = accessLevelHierarchy.indexOf(mostPermissive.data_access as DataAccessLevel);
          const currentIndex = accessLevelHierarchy.indexOf(current.data_access as DataAccessLevel);
          return currentIndex < mostPermissiveIndex ? current : mostPermissive;
        });

        // Find the role ID that granted this permission
        const grantingRoleId = roleIds.find(roleId => 
          relevantPermissions.some(p => 
            p.role_id === roleId && 
            p.data_access === mostPermissivePermission.data_access &&
            p.resource === mostPermissivePermission.resource &&
            p.verb === mostPermissivePermission.verb
          )
        ) || null;

        return ok({
          effectiveDataAccessLevel: mostPermissivePermission.data_access as DataAccessLevel,
          effectiveRoleId: grantingRoleId,
        });
      },
      err: async (error) => err(error),
    });
  }

  /**
   * @summary
   * <blockquote>Checks if a permission verb matches or is more permissive than the required verb.</blockquote>
   * 
   * @param permissionVerb - The permission verb to check
   * @param requiredVerb - The required verb
   * @returns True if the permission verb matches or is more permissive
   * 
   * @internal
   */
  private verbMatches(permissionVerb: PermissionVerb, requiredVerb: PermissionVerb): boolean {
    // Use the permissionOrder to determine if permissionVerb is as or more permissive than requiredVerb
    // If permissionVerb's index >= requiredVerb's index, it is more permissive or equal
    return permissionOrder.indexOf(permissionVerb) >= permissionOrder.indexOf(requiredVerb);
  }
}